/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  coverageListResponse,
  createCoveragePayload,
  updateCoveragePayload,
} from 'interfaces/Coverage';
import { errorRedux } from 'interfaces/redux';
import {
  createCoverage,
  deleteCoverage,
  getCoverageById,
  getCoverageList,
  updateCoverage,
} from './service';

// Action: Listar coberturas
export const coverageList = createAsyncThunk<
  coverageListResponse,
  undefined,
  { rejectValue: errorRedux }
>('coverage/list', async (_, { rejectWithValue }) => {
  try {
    const data = await getCoverageList();
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Buscar cobertura por ID
export const coverageById = createAsyncThunk<
  coverageListResponse,
  number,
  { rejectValue: errorRedux }
>('coverage/getById', async (id, { rejectWithValue }) => {
  try {
    const data = await getCoverageById(id);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Criar cobertura
export const coverageCreate = createAsyncThunk<
  coverageListResponse,
  createCoveragePayload,
  { rejectValue: errorRedux }
>('coverage/create', async (payload, { rejectWithValue }) => {
  try {
    const data = await createCoverage(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Atualizar cobertura
export const coverageUpdate = createAsyncThunk<
  coverageListResponse,
  updateCoveragePayload,
  { rejectValue: errorRedux }
>('coverage/update', async (payload, { rejectWithValue }) => {
  try {
    const data = await updateCoverage(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Deletar cobertura
export const coverageDelete = createAsyncThunk<void, number, { rejectValue: errorRedux }>(
  'coverage/delete',
  async (id, { rejectWithValue }) => {
    try {
      await deleteCoverage(id);
      return undefined;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

// Reducer
interface ICoverage extends errorRedux {
  coverageList: coverageListResponse;
  coverageById: coverageListResponse;
}

const initialState: ICoverage = {
  status: 'idle',
  message: '',
  coverageList: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  coverageById: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
};

export const CoverageReducer = createSlice({
  name: 'CoverageStatus',
  initialState,
  reducers: {
    clearCoverageStatus: (state) => {
      state.status = 'idle';
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(coverageList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(coverageList.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.coverageList = action.payload;
      })
      .addCase(coverageList.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(coverageById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(coverageById.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.coverageById = action.payload;
      })
      .addCase(coverageById.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(coverageCreate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(coverageCreate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(coverageCreate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(coverageUpdate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(coverageUpdate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(coverageUpdate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(coverageDelete.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(coverageDelete.fulfilled, (state) => {
        state.status = 'completed';
        state.message = 'Cobertura deletada com sucesso';
      })
      .addCase(coverageDelete.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      });
  },
});

export const { clearCoverageStatus } = CoverageReducer.actions;
export default CoverageReducer.reducer;
